import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { House, Rows3, Building2, Landmark, Hotel, Warehouse, ArrowLeft, ArrowRight, Loader2, PartyPopper, CalendarDays, Tag } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { FadeUp, EASE } from "@/components/Reveal";
import { PRICE_NOTE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WONINGTYPES = [
  { value: "Appartement", icon: Hotel, price: 139 },
  { value: "Tussenwoning", icon: Rows3, price: 149 },
  { value: "Hoekwoning", icon: House, price: 159 },
  { value: "2-onder-1-kapwoning", icon: Warehouse, price: 169 },
  { value: "Vrijstaande woning", icon: Landmark, price: 199 },
  { value: "Maisonnette / overig", icon: Building2, price: null },
];

const STEPS = ["Woningtype", "Woninggegevens", "Contactgegevens", "Overzicht"];

const inputClass =
  "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-muted-foreground/60 transition-[border-color,box-shadow] duration-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

const initialForm = {
  woningtype: "",
  postcode: "",
  huisnummer: "",
  straat: "",
  plaats: "",
  bouwjaar: "",
  woonoppervlakte: "",
  voorkeursdatum: "",
  voornaam: "",
  achternaam: "",
  email: "",
  telefoon: "",
  opmerking: "",
};

function Field({ id, label, required = true, testid, ...props }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-bold text-ink">
        {label} {required && <span aria-hidden="true">*</span>}
      </label>
      <input id={id} data-testid={testid} className={inputClass} {...props} />
    </div>
  );
}

export default function Aanvragen() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  useEffect(() => {
    const t = searchParams.get("type");
    if (t && WONINGTYPES.some((w) => w.value === t)) {
      setForm((f) => ({ ...f, woningtype: t }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selected = WONINGTYPES.find((w) => w.value === form.woningtype);
  const prijsindicatie = selected
    ? selected.price
      ? `vanaf €${selected.price} incl. btw`
      : "op aanvraag"
    : "";

  const stepValid = [
    form.woningtype !== "",
    form.postcode.trim().length >= 6 && form.huisnummer.trim() && form.straat.trim() && form.plaats.trim() && form.bouwjaar.trim() && form.woonoppervlakte.trim() && form.voorkeursdatum,
    form.voornaam.trim() && form.achternaam.trim() && /\S+@\S+\.\S+/.test(form.email) && form.telefoon.trim().length >= 8,
    true,
  ][step];

  const next = () => {
    if (!stepValid) return;
    setDir(1);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setDir(-1);
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/requests`, { ...form, prijsindicatie });
      setReference(res.data.request_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Verzenden is niet gelukt. Controleer uw gegevens en probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (reference) {
    return (
      <>
        <SEO title="Aanvraag ontvangen | Directelabels" description="Uw aanvraag voor een energielabel is ontvangen." />
        <section data-testid="aanvraag-success" className="mx-auto max-w-2xl px-5 py-24 text-center md:py-32">
          <FadeUp>
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-energy-pale text-primary">
              <PartyPopper size={36} />
            </span>
            <h1 className="mt-8 font-display text-4xl font-semibold tracking-tighter text-ink md:text-5xl">Aanvraag ontvangen</h1>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Bedankt {form.voornaam}! Uw aanvraag is goed binnengekomen. Wij nemen op werkdagen binnen 24 uur contact
              met u op om de woningopname in te plannen. U ontvangt tevens een bevestiging per e-mail.
            </p>
            <div className="mt-8 inline-block rounded-2xl border border-primary/20 bg-energy-pale px-8 py-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Uw referentienummer</p>
              <p data-testid="aanvraag-reference" className="mt-1 font-display text-2xl font-semibold tracking-tight text-energy-deep">{reference}</p>
            </div>
          </FadeUp>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Energielabel aanvragen | Directelabels Rotterdam"
        description="Vraag in vier eenvoudige stappen een energielabel aan voor uw woning in Rotterdam en Zuid-Holland. Duidelijke introductietarieven vanaf €139 incl. btw."
        path="/aanvragen"
      />

      <section data-testid="aanvragen-page" className="mx-auto max-w-3xl px-5 py-16 md:py-24">
        <FadeUp>
          <p className="text-center text-xs font-bold uppercase tracking-[0.24em] text-primary">Energielabel aanvragen</p>
          <h1 className="mt-4 text-center font-display text-4xl font-semibold tracking-tighter text-ink md:text-5xl">
            Uw aanvraag in vier stappen
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-center text-sm leading-relaxed text-muted-foreground md:text-base">
            Vul de gegevens hieronder in. Dit duurt ongeveer twee minuten. Daarna nemen wij contact met u op om de
            woningopname in te plannen.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <ol data-testid="step-indicator" className="mt-12 flex items-center gap-2" aria-label="Voortgang aanvraag">
            {STEPS.map((label, i) => (
              <li key={label} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-primary" : "bg-black/10"}`} aria-hidden="true" />
                <p className={`mt-2 text-center text-[11px] font-bold uppercase tracking-wider md:text-xs ${i <= step ? "text-primary" : "text-muted-foreground/60"}`}>
                  {label}
                </p>
              </li>
            ))}
          </ol>
        </FadeUp>

        <FadeUp delay={0.25}>
          <div className="mt-10 rounded-3xl border border-black/5 bg-white p-6 shadow-sm md:p-10">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={step}
                custom={dir}
                initial={{ opacity: 0, x: 40 * dir }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 * dir }}
                transition={{ duration: 0.35, ease: EASE }}
              >
                {step === 0 && (
                  <fieldset data-testid="step-woningtype">
                    <legend className="font-display text-xl font-medium tracking-tight text-ink md:text-2xl">Wat voor type woning heeft u?</legend>
                    <p className="mt-2 text-sm text-muted-foreground">U ziet direct de bijbehorende prijsindicatie.</p>
                    <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
                      {WONINGTYPES.map((w) => {
                        const active = form.woningtype === w.value;
                        return (
                          <button
                            key={w.value}
                            type="button"
                            data-testid={`woningtype-${w.value.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            aria-pressed={active}
                            onClick={() => setForm((f) => ({ ...f, woningtype: w.value }))}
                            className={`flex flex-col items-center gap-2.5 rounded-2xl border p-6 text-center transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${
                              active ? "border-primary bg-energy-pale" : "border-black/10 bg-white hover:border-primary/40"
                            }`}
                          >
                            <w.icon size={26} className={active ? "text-primary" : "text-ink/50"} />
                            <span className={`text-sm font-bold leading-snug ${active ? "text-energy-deep" : "text-ink/70"}`}>{w.value}</span>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${active ? "bg-primary text-white" : "bg-energy-pale text-energy-deep"}`}>
                              <Tag size={11} />
                              {w.price ? `vanaf €${w.price}` : "op aanvraag"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                )}

                {step === 1 && (
                  <fieldset data-testid="step-woninggegevens">
                    <legend className="font-display text-xl font-medium tracking-tight text-ink md:text-2xl">Vertel ons over uw woning</legend>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      <Field id="f-postcode" label="Postcode" testid="input-postcode" placeholder="3011 AA" value={form.postcode} onChange={set("postcode")} autoComplete="postal-code" />
                      <Field id="f-huisnummer" label="Huisnummer" testid="input-huisnummer" placeholder="12" value={form.huisnummer} onChange={set("huisnummer")} />
                      <Field id="f-straat" label="Straatnaam" testid="input-straat" placeholder="Voorbeeldstraat" value={form.straat} onChange={set("straat")} autoComplete="street-address" />
                      <Field id="f-plaats" label="Plaats" testid="input-plaats" placeholder="Rotterdam" value={form.plaats} onChange={set("plaats")} />
                      <Field id="f-bouwjaar" label="Bouwjaar" testid="input-bouwjaar" type="number" min="1600" max="2030" placeholder="1985" value={form.bouwjaar} onChange={set("bouwjaar")} />
                      <Field id="f-oppervlakte" label="Woonoppervlakte (m²)" testid="input-woonoppervlakte" type="number" min="10" placeholder="110" value={form.woonoppervlakte} onChange={set("woonoppervlakte")} />
                      <div className="sm:col-span-2">
                        <label htmlFor="f-datum" className="mb-2 block text-sm font-bold text-ink">Gewenste datum opname *</label>
                        <div className="relative">
                          <CalendarDays size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input
                            id="f-datum"
                            type="date"
                            data-testid="input-voorkeursdatum"
                            className={`${inputClass} pl-11`}
                            value={form.voorkeursdatum}
                            onChange={set("voorkeursdatum")}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </div>
                      </div>
                    </div>
                  </fieldset>
                )}

                {step === 2 && (
                  <fieldset data-testid="step-contactgegevens">
                    <legend className="font-display text-xl font-medium tracking-tight text-ink md:text-2xl">Hoe kunnen wij u bereiken?</legend>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      <Field id="f-voornaam" label="Voornaam" testid="input-voornaam" placeholder="Jan" value={form.voornaam} onChange={set("voornaam")} autoComplete="given-name" />
                      <Field id="f-achternaam" label="Achternaam" testid="input-achternaam" placeholder="de Vries" value={form.achternaam} onChange={set("achternaam")} autoComplete="family-name" />
                      <Field id="f-email" label="E-mailadres" testid="input-email" type="email" placeholder="jan@voorbeeld.nl" value={form.email} onChange={set("email")} autoComplete="email" />
                      <Field id="f-telefoon" label="Telefoonnummer" testid="input-telefoon" type="tel" placeholder="06 - 12 34 56 78" value={form.telefoon} onChange={set("telefoon")} autoComplete="tel" />
                      <div className="sm:col-span-2">
                        <label htmlFor="f-opmerking" className="mb-2 block text-sm font-bold text-ink">Eventuele opmerkingen</label>
                        <textarea
                          id="f-opmerking"
                          data-testid="input-opmerking"
                          rows={4}
                          className={inputClass}
                          placeholder="Bijvoorbeeld: spoedaanvraag i.v.m. naderende verkoop"
                          value={form.opmerking}
                          onChange={set("opmerking")}
                        />
                      </div>
                    </div>
                  </fieldset>
                )}

                {step === 3 && (
                  <div data-testid="step-overzicht">
                    <h2 className="font-display text-xl font-medium tracking-tight text-ink md:text-2xl">Controleer uw aanvraag</h2>
                    <dl className="mt-8 divide-y divide-black/5 rounded-2xl border border-black/10">
                      {[
                        ["Woningtype", form.woningtype],
                        ["Prijsindicatie", prijsindicatie],
                        ["Adres", `${form.straat} ${form.huisnummer}, ${form.postcode} ${form.plaats}`],
                        ["Bouwjaar", form.bouwjaar],
                        ["Woonoppervlakte", `${form.woonoppervlakte} m²`],
                        ["Gewenste datum", form.voorkeursdatum],
                        ["Naam", `${form.voornaam} ${form.achternaam}`],
                        ["E-mailadres", form.email],
                        ["Telefoonnummer", form.telefoon],
                        ...(form.opmerking ? [["Opmerking", form.opmerking]] : []),
                      ].map(([k, v], i) => (
                        <div key={k} data-testid={`overzicht-row-${i}`} className="grid gap-1 px-5 py-4 sm:grid-cols-3">
                          <dt className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{k}</dt>
                          <dd className={`text-sm font-semibold sm:col-span-2 ${k === "Prijsindicatie" ? "text-primary" : "text-ink"}`}>{v}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="mt-5 text-xs leading-relaxed text-muted-foreground">{PRICE_NOTE}</p>
                    <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                      Door te versturen gaat u akkoord met het verwerken van uw gegevens zoals beschreven in onze
                      privacyverklaring. De aanvraag is vrijblijvend; u ontvangt eerst een definitieve offerte.
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center justify-between gap-4">
              {step > 0 ? (
                <button
                  type="button"
                  data-testid="btn-vorige"
                  onClick={back}
                  className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-bold text-ink transition-colors duration-200 hover:border-primary hover:text-primary"
                >
                  <ArrowLeft size={16} />
                  Vorige
                </button>
              ) : (
                <span />
              )}
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  data-testid="btn-volgende"
                  onClick={next}
                  disabled={!stepValid}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Volgende
                  <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="btn-versturen"
                  onClick={submit}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "Versturen..." : "Aanvraag versturen"}
                </button>
              )}
            </div>
          </div>
        </FadeUp>
      </section>
    </>
  );
}
